import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Container, Stack } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid';

import PromoAiWorkflowEntry from '~/components/Common/Blocks/PromoAiWorkflowEntry';
import usePromoAdminPermission from '~/components/Promo/hooks/usePromoPermission';

import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { GetPromoCodeProps, expirePromo, getPromoCodes, isPromoCategory } from '~/services/PromoService';

import PageSubheader from '../Common/Elements/PageSubheader';
import PromoSearchForm from '../Common/Forms/PromoSearchForm';
import Spinner from '../Common/Spinner';
import { withTenant } from '../hoc';

import PromoBulkExpiryUpdate from './PromoBulkExpiryUpdate';
import PromoBulkOperations, { BulkOperation, BulkOperationRowStatus } from './PromoBulkOperations';
import PromoPage from './PromoPage';
import { queryParamNames, usePromoCodes } from './hooks/usePromoCodes';
import usePromoMeta from './hooks/usePromoMeta';
import { isPromoBrand } from './promoBrandUtils';
import { promoIdPath } from './promoUrls';

const SIZE_PER_PAGE = 10;
const REFINED_DISPLAY_MODE = 'refined';

export interface PromoResult {
  status: number;
  message: string;
  count: number;
  total: number;
  result: Array<App.PromoData>;
}

interface Props {
  tenant: App.Tenant;
  disableContainer?: boolean;
}

type Users = {
  [id: string]: App.User;
};

const onClick = (row: GridCellParams<App.PromoData>) => {
  // 'code_name' handles the click event internally via Link elements
  if (row.field === 'code_name' || row.field == 'active' || row.field == 'default_display') {
    return;
  }
  const url = promoIdPath(row.row.id_promo_code);
  window.open(url, '_blank');
};

const getQueryFilters = (query: URLSearchParams): GetPromoCodeProps => {
  const page = query.get(queryParamNames.page) ? parseInt(query.get(queryParamNames.page)) : 1;
  const limit = query.get(queryParamNames.limit);
  const codeName = query.get(queryParamNames.name) || '';
  const emailAddress = query.get(queryParamNames.emailAddress) || '';
  const caseId = query.get(queryParamNames.caseId) || '';
  const expiresAfter = query.get(queryParamNames.expiresAfter) || '';
  const noChildPromos = query.get(queryParamNames.noChildPromos) === 'true';
  const onlyChildPromos = query.get(queryParamNames.onlyChildPromos) === 'true';
  const excludeReferralPromos = query.get(queryParamNames.excludeReferralPromos) === 'true';
  const userIdInteracted = query.get(queryParamNames.userIdInteracted) || '';
  const deptTag = query.get(queryParamNames.deptTag) || '';
  const categoryTag = query.get(queryParamNames.categoryTag) || '';
  const subCategoryTag = query.get(queryParamNames.subCategoryTag) || '';
  const promoCategoryValue = query.get(queryParamNames.promoCategory);
  const promoCategory = isPromoCategory(promoCategoryValue) ? promoCategoryValue : undefined;
  const onlyParentPromos = query.get(queryParamNames.onlyParentPromos) === 'true';
  const isDev = query.get(queryParamNames.dev) || '';

  return {
    page,
    codeName,
    emailAddress,
    caseId,
    expiresAfter,
    noChildPromos,
    onlyChildPromos,
    excludeReferralPromos,
    userIdInteracted,
    deptTag,
    categoryTag,
    subCategoryTag,
    promoCategory,
    onlyParentPromos,
    brand: query.get('brand') ?? 'luxuryescapes',
    limit: limit ? parseInt(limit) : SIZE_PER_PAGE,
    dev: isDev === 'true',
  };
};

const setQueryFilters = (push: (to: { pathname: string; search: string }) => void, filters: GetPromoCodeProps) => {
  const query = new URLSearchParams();

  query.set(queryParamNames.page, filters.page.toString());
  query.set(queryParamNames.limit, filters.limit.toString());
  query.set(queryParamNames.name, filters.codeName);
  if (filters.emailAddress) {
    query.set(queryParamNames.emailAddress, filters.emailAddress);
  }
  if (filters.caseId) {
    query.set(queryParamNames.caseId, filters.caseId);
  }

  query.set(queryParamNames.expiresAfter, filters.expiresAfter);
  if (filters.noChildPromos) {
    query.set(queryParamNames.noChildPromos, filters.noChildPromos.toString());
  }
  if (filters.onlyChildPromos) {
    query.set(queryParamNames.onlyChildPromos, 'true');
  }

  if (filters.excludeReferralPromos) {
    query.set(queryParamNames.excludeReferralPromos, filters.excludeReferralPromos.toString());
  }

  if (filters.userIdInteracted) {
    query.set(queryParamNames.userIdInteracted, filters.userIdInteracted);
  }

  if (filters.deptTag) {
    query.set(queryParamNames.deptTag, filters.deptTag);
  }

  if (filters.categoryTag) {
    query.set(queryParamNames.categoryTag, filters.categoryTag);
  }

  if (filters.subCategoryTag) {
    query.set(queryParamNames.subCategoryTag, filters.subCategoryTag);
  }
  if (filters.promoCategory) {
    query.set(queryParamNames.promoCategory, filters.promoCategory);
  }
  if (filters.onlyParentPromos) {
    query.set(queryParamNames.onlyParentPromos, 'true');
  }

  if (filters.dev) {
    query.set(queryParamNames.dev, filters.dev.toString());
  }
  query.set(queryParamNames.brand, filters.brand);
  push({
    pathname: '/marketing/search-promo',
    search: query.toString(),
  });
  return query;
};

function PromoSearchContainer({ tenant: { brand }, disableContainer = false }: Props) {
  const query = useQuery();
  const { push } = useAppNavigation();
  const isDev = !!query.get('dev');
  const isRefined = query.get('displayMode') === REFINED_DISPLAY_MODE;
  const isPromoAdmin = usePromoAdminPermission();

  const initialFilters = getQueryFilters(query);

  const { promoMeta } = usePromoMeta();

  const { promoCodes, isLoading, fetchData, filters, setFilters, total } = usePromoCodes({
    initialFilters,
  });
  const [bulkOperation, setBulkOperation] = useState<BulkOperation | null>(null);
  const [bulkRowStatuses, setBulkRowStatuses] = useState<Record<string, BulkOperationRowStatus>>({});
  useEffect(() => {
    if (
      filters.codeName ||
      filters.caseId ||
      filters.userIdInteracted ||
      filters.deptTag ||
      filters.categoryTag ||
      filters.subCategoryTag ||
      filters.promoCategory ||
      filters.onlyChildPromos ||
      filters.onlyParentPromos
    ) {
      fetchData(filters);
      setQueryFilters(push, {
        ...filters,
        dev: filters.dev,
      });
    }
  }, [filters, fetchData, push]);

  const expirePromoWithBrand = useCallback(
    async (id: string) => {
      await expirePromo(id, brand);
      await fetchData(filters);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand, filters],
  );

  const setCurrentPage = useCallback(
    (page: number, pageSize: number) => {
      const newFilters: GetPromoCodeProps = { ...filters, limit: pageSize, page: page + 1, dev: isDev };
      setQueryFilters(push, newFilters);
      setFilters(newFilters);
      fetchData(newFilters);
    },
    [filters, fetchData, isDev, push, setFilters],
  );

  const handleSearch = useCallback(
    (filters: GetPromoCodeProps) => {
      const newFilters = { ...filters, page: 1, isDev };
      setQueryFilters(push, newFilters);
      setFilters(newFilters);
      fetchData(newFilters);
    },
    [fetchData, isDev, push, setFilters],
  );

  const handleBulkOperationChange = useCallback((operation: BulkOperation | null) => {
    setBulkOperation(operation);
  }, []);

  const loadSearchPage = useCallback(
    async (page: number, batchSize: number) => {
      const response = await getPromoCodes({ ...filters, page, limit: batchSize });
      return response.result;
    },
    [filters],
  );

  const isUnfilteredAllSearch =
    filters.codeName.toUpperCase() === 'ALL' &&
    !filters.emailAddress &&
    !filters.caseId &&
    !filters.userIdInteracted &&
    !filters.deptTag &&
    !filters.categoryTag &&
    !filters.subCategoryTag &&
    !filters.noChildPromos &&
    !filters.onlyChildPromos &&
    !filters.excludeReferralPromos &&
    !filters.excludeApprovedPromos;

  return (
    <Container className="promo-codes T-promo-page">
      <Helmet>
        <title>Search Promo Codes</title>
      </Helmet>

      <PageSubheader title={`${total ? `${total} ` : 'Search '} Promo Codes`} />

      <Stack direction="column" gap={4}>
        <PromoAiWorkflowEntry kind="promo" />
        <PromoSearchForm searchQuery={handleSearch} isLoading={isLoading} />
        {isLoading && <Spinner />}

        {total ? `${total} promos found` : 'No promos found'}
        {promoCodes !== null && (
          <PromoPage
            promos={promoCodes}
            total={total}
            isLoading={isLoading}
            page={filters.page}
            sizePerPage={filters.limit}
            isDev={filters.dev}
            onPageChange={setCurrentPage}
            onClick={onClick}
            expirePromo={expirePromoWithBrand}
            promoMeta={promoMeta}
            bulkOperation={bulkOperation}
            bulkRowStatuses={bulkRowStatuses}
            refined={isRefined}
          />
        )}
        {isPromoBrand(brand) && isPromoAdmin && !isUnfilteredAllSearch && promoCodes !== null && (
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
            <PromoBulkExpiryUpdate
              activeOperation={bulkOperation}
              currentPagePromos={promoCodes}
              totalSearchResults={total}
              isUnfilteredAllSearch={isUnfilteredAllSearch}
              searchFilters={filters}
              loadSearchPage={loadSearchPage}
              onComplete={() => fetchData(filters)}
              onOperationChange={handleBulkOperationChange}
              onStatusChange={setBulkRowStatuses}
            />
            <PromoBulkOperations
              activeOperation={bulkOperation}
              currentPagePromos={promoCodes}
              totalSearchResults={total}
              isUnfilteredAllSearch={isUnfilteredAllSearch}
              searchFilters={filters}
              loadSearchPage={loadSearchPage}
              onComplete={() => fetchData(filters)}
              onOperationChange={handleBulkOperationChange}
              onStatusChange={setBulkRowStatuses}
            />
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default withTenant(PromoSearchContainer);
